import { Button } from '@listening/frontend/components/radix/button';
import { cn } from '@listening/utils/react-helpers';
import type { ReactNode } from 'react';
import { clientEnv } from '@/env';
import * as m from '@/paraglide/messages';
import { localizeHref } from '@/paraglide/runtime';
import type { MarketingPricing } from '@/server/services/pricing-service';

function Feature({
  children,
  unavailable = false,
}: {
  children: ReactNode;
  unavailable?: boolean;
}) {
  return (
    <li className='flex gap-2.75'>
      <span aria-hidden='true' className='shrink-0 text-[15px]'>
        {unavailable ? '×' : '✓'}
      </span>
      <span>{children}</span>
    </li>
  );
}

function Features() {
  return (
    <ul className='m-0 grid list-none gap-3 p-0 text-sm leading-[1.45]'>
      <Feature>{m.pricing_feature_audio()}</Feature>
      {clientEnv.zoteroUiEnabled && (
        <Feature>{m.pricing_feature_zotero()}</Feature>
      )}
      <Feature>{m.pricing_feature_extension()}</Feature>
      <Feature>
        {clientEnv.desktopDownloadsEnabled
          ? m.pricing_feature_devices()
          : m.pricing_benefit_web_mobile_description()}
      </Feature>
      <Feature>{m.pricing_feature_voice()}</Feature>
      <Feature>{m.pricing_feature_notes()}</Feature>
    </ul>
  );
}

function Plan({
  yearly,
  pricing,
  className,
}: {
  yearly: boolean;
  pricing: MarketingPricing;
  className?: string | undefined;
}) {
  const { trialDays } = pricing;
  const amount = yearly ? pricing.yearlyPerMonthUsd : pricing.monthlyUsd;
  const savings =
    pricing.monthlyUsd > 0
      ? Math.round((1 - pricing.yearlyUsd / (pricing.monthlyUsd * 12)) * 100)
      : 0;
  const titleId = yearly ? 'pricing-yearly-title' : 'pricing-monthly-title';
  const cta =
    trialDays > 0
      ? m.pricing_start_trial({ days: trialDays })
      : yearly
        ? m.pricing_choose_yearly()
        : m.pricing_choose_monthly();
  const billing = yearly
    ? trialDays > 0
      ? m.pricing_annual_after_trial({ amount: pricing.yearlyUsd.toFixed(2) })
      : m.pricing_annual_paid({ amount: pricing.yearlyUsd.toFixed(2) })
    : trialDays > 0
      ? m.pricing_monthly_after_trial()
      : m.pricing_monthly_billing();
  return (
    <article
      aria-labelledby={titleId}
      className={cn(
        'flex min-w-0 flex-col rounded-[15px] border border-(--pricing-border) bg-(--pricing-paper) p-6.25 min-[641px]:min-h-127.5 min-[641px]:p-7.5',
        yearly
          ? 'relative border-(--pricing-blue-border) bg-(--pricing-blue-pale) min-[641px]:min-h-136.5 min-[641px]:py-11.75'
          : 'min-[641px]:-ml-px min-[641px]:rounded-l-none',
        className,
      )}
    >
      <div className='flex min-h-7.5 flex-wrap items-center gap-3'>
        <h2
          id={titleId}
          className='text-[27px] leading-[normal] font-[620] tracking-[-0.5px]'
        >
          {yearly ? m.pricing_tier_yearly() : m.common_monthly()}
        </h2>
        {yearly && savings > 0 && (
          <span className='rounded-[3px] border border-(--pricing-blue)/60 px-1.5 py-0.75 text-[11px] leading-none font-semibold text-(--pricing-blue) uppercase'>
            {m.pricing_save_percent({ percent: String(savings) })}
          </span>
        )}
      </div>
      <p className='mt-4.5 flex items-center gap-2'>
        <strong className='text-[49px] leading-[normal] font-[420] tracking-[-2px]'>
          ${amount.toFixed(2)}
        </strong>
        <span className='w-12 text-xs leading-[1.1] text-(--pricing-muted)'>
          {m.pricing_usd_month()}
        </span>
      </p>
      <p
        className={cn(
          'mt-1.25 min-h-4.75 text-xs leading-[1.55] text-(--pricing-muted)',
          yearly && 'text-[13px] font-[550] text-(--pricing-ink)',
        )}
      >
        {billing}
      </p>
      <p className='mt-5 text-[15px] leading-normal'>
        {yearly
          ? m.pricing_yearly_description()
          : m.pricing_monthly_description()}
      </p>
      <div className='py-5.5'>
        <Button
          asChild
          className={cn(
            'h-11.5 w-full rounded-full border-0 px-3.75 text-sm leading-[normal] font-[550] text-(--pricing-paper) shadow-none',
            yearly
              ? 'bg-(--pricing-blue) hover:bg-(--pricing-blue)/95'
              : 'bg-(--pricing-ink) hover:bg-(--pricing-ink)/95',
          )}
        >
          <a
            href={localizeHref(
              `/app/continue?billingPeriod=${yearly ? 'yearly' : 'monthly'}&redirectTo=%2Fapp%2Fpremium`,
            )}
          >
            {cta}
          </a>
        </Button>
        {trialDays > 0 && (
          <p className='mt-2.5 text-center text-[11px] leading-normal text-(--pricing-muted)'>
            <span className='block'>{m.pricing_trial_eligibility()}</span>
            {m.pricing_cancel_trial()}
          </p>
        )}
      </div>
      <Features />
    </article>
  );
}

function FreePlan({ pending = false }: { pending?: boolean }) {
  return (
    <article
      aria-labelledby='pricing-free-title'
      className={cn(
        'flex min-w-0 flex-col rounded-[15px] border border-(--pricing-border) bg-(--pricing-paper) p-6.25 min-[641px]:p-7.5 min-[1025px]:row-span-6 min-[1025px]:row-start-1 min-[1025px]:my-4.5 min-[1025px]:-mr-px min-[1025px]:grid min-[1025px]:min-h-127.5 min-[1025px]:grid-rows-subgrid min-[1025px]:rounded-r-none',
        pending && 'hidden min-[1025px]:invisible',
      )}
    >
      <h2
        id='pricing-free-title'
        className='text-[27px] font-[620] tracking-[-0.5px]'
      >
        {m.plan_free()}
      </h2>
      <p className='mt-4.5 flex items-center gap-2'>
        <strong className='text-[49px] font-[420] tracking-[-2px]'>$0</strong>
        <span className='w-12 text-xs leading-[1.1] text-(--pricing-muted)'>
          {m.pricing_usd_month()}
        </span>
      </p>
      <p className='mt-1.25 text-xs leading-[1.55] text-(--pricing-muted)'>
        {m.pricing_tier_free_description()}
      </p>
      <p className='mt-5 text-[15px] leading-normal'>
        {m.pricing_free_description()}
      </p>
      <div className='py-5.5'>
        <Button
          asChild
          className='h-11.5 w-full rounded-full bg-(--pricing-ink) text-(--pricing-paper) hover:bg-(--pricing-ink)/95'
        >
          <a href={localizeHref('/app/continue')}>{m.pricing_choose_free()}</a>
        </Button>
      </div>
      <ul className='m-0 grid list-none gap-3 p-0 text-sm leading-[1.45]'>
        <Feature>{m.pricing_tier_free_feature_1()}</Feature>
        <Feature>{m.pricing_tier_free_feature_2()}</Feature>
        <Feature unavailable>{m.pricing_tier_free_feature_3()}</Feature>
        <Feature unavailable>{m.pricing_tier_free_feature_4()}</Feature>
        <Feature unavailable>{m.pricing_tier_free_feature_5()}</Feature>
      </ul>
    </article>
  );
}

export function PricingPlans({
  pricing,
  showFreePlan = false,
  freePlanPending = false,
}: {
  pricing: MarketingPricing;
  showFreePlan?: boolean;
  freePlanPending?: boolean;
}) {
  const freePlanSlot = showFreePlan || freePlanPending;
  return (
    <section
      id='pricing-plans'
      aria-labelledby='pricing-plans-heading'
      className='grid min-h-[calc(100svh-72px)] content-center px-5 pt-10 pb-15 min-[641px]:px-8 min-[641px]:pt-6 min-[641px]:pb-8 min-[1920px]:min-h-[min(calc(100svh-72px),900px)]'
    >
      <div className='mx-auto w-full max-w-257.5 min-[1920px]:max-w-320'>
        <header className='mb-7.5 text-center min-[641px]:mb-9'>
          <h1
            id='pricing-plans-heading'
            className='text-[29px] leading-[1.25] font-semibold tracking-[-1px] min-[641px]:text-[34px]'
          >
            {m.pricing_hero_title()}
          </h1>
          <p className='mt-2.5 text-[13px] leading-[normal] text-(--pricing-muted) min-[641px]:text-sm'>
            {m.pricing_hero_subtitle()}
          </p>
        </header>
        <div
          className={cn(
            'grid gap-4.5 min-[641px]:grid-cols-2 min-[641px]:items-center min-[641px]:gap-0',
            freePlanSlot &&
              'min-[1025px]:grid-cols-3 min-[1025px]:items-stretch',
          )}
        >
          <Plan
            yearly
            pricing={pricing}
            className={
              freePlanSlot
                ? 'min-[1025px]:order-1 min-[1025px]:row-span-6 min-[1025px]:row-start-1 min-[1025px]:grid min-[1025px]:grid-rows-subgrid min-[1025px]:py-12'
                : undefined
            }
          />
          <Plan
            yearly={false}
            pricing={pricing}
            className={
              freePlanSlot
                ? 'min-[1025px]:order-2 min-[1025px]:row-span-6 min-[1025px]:row-start-1 min-[1025px]:my-4.5 min-[1025px]:grid min-[1025px]:grid-rows-subgrid'
                : undefined
            }
          />
          {freePlanSlot && <FreePlan pending={!showFreePlan} />}
        </div>
      </div>
    </section>
  );
}
